import { Fragment } from "react"

import { Card, Checkbox, Divider, Flex, Text } from "@/atoms"
import { Trans, useTranslation } from "@/hooks/useTranslation"
import { CheckboxSettingsGroup } from "@/pages/IntegrationsPage/components/molecules/CheckboxSettingsGroup"
import { InfoCircleOutlined } from "@ant-design/icons"

import styles from "./TicketingNotificationFields.module.scss"

import type { CheckboxSettingsItem } from "@/pages/IntegrationsPage/components/molecules/CheckboxSettingsGroup"
import type { TicketingNotificationFieldsProps } from "./TicketingNotificationFields.types"

// The checkbox groups of a form whose values come from ticketingSettingsAdapter
export const TicketingNotificationFields = (
  props: TicketingNotificationFieldsProps
) => {
  const { keyPrefix, vendorName, undismissed, className } = props
  const { t } = useTranslation("translation", { keyPrefix })
  const values = { vendor: vendorName }
  const transComponents = { mono: <span className={styles.mono} /> }

  const settingsItems: CheckboxSettingsItem[] = [
    {
      key: "resolved",
      label: t("resolved.title", values),
      type: "checkbox-group",
      children: (
        <>
          <Checkbox value="comment">
            <Trans components={transComponents}>
              {t("resolved.comment", values)}
            </Trans>
          </Checkbox>
          <Checkbox value="label">
            <Trans components={transComponents}>
              {t("resolved.label", values)}
            </Trans>
          </Checkbox>
        </>
      )
    },
    {
      key: "dismissed",
      label: t("dismissed.title", values),
      type: "checkbox-group",
      children: (
        <>
          <Checkbox value="comment">
            <Trans components={transComponents}>
              {t("dismissed.comment", values)}
            </Trans>
          </Checkbox>
          <Checkbox value="label">
            <Trans components={transComponents}>
              {t("dismissed.label", values)}
            </Trans>
          </Checkbox>
          {undismissed && (
            <>
              <Checkbox value="undismissedComment">
                <Trans components={transComponents}>
                  {t("undismissed.comment", values)}
                </Trans>
              </Checkbox>
              <Checkbox value="undismissedLabel">
                <Trans components={transComponents}>
                  {t("undismissed.label", values)}
                </Trans>
              </Checkbox>
            </>
          )}
        </>
      )
    },
    {
      key: "riskChange",
      label: t("riskChange.title", values),
      type: "checkbox-group",
      gap: 0, // override default gap for this item
      children: (
        <>
          <Checkbox value="comment">
            <Trans components={transComponents}>
              {t("riskChange.comment", values)}
            </Trans>
          </Checkbox>
          <div className={styles.hint}>{t("riskChange.hint", values)}</div>
        </>
      )
    }
  ]

  return (
    <Flex vertical gap={16}>
      <Text type="secondary" className={className}>
        {t("description", values)}
      </Text>

      <Card className={styles.card}>
        {settingsItems.map((item, index) => (
          <Fragment key={item.key}>
            {index > 0 && <Divider className={styles.divider} />}
            <CheckboxSettingsGroup items={[item]} />
          </Fragment>
        ))}
      </Card>

      <div className={styles.note}>
        <InfoCircleOutlined />
        <Text type="secondary">{t("note", values)}</Text>
      </div>
    </Flex>
  )
}
